// AntScore — обёртка над Web Speech API (window.speechSynthesis) для русской озвучки табло.
//
// Назначение:
//   - выбрать голос ru-RU (с фолбэком на любой доступный голос);
//   - speak(text, { flush }) — произнести фразу, по умолчанию прерывая предыдущую (flush);
//   - тумблер звука (enabled): при выключенном звуке ничего не произносим;
//   - тихий фолбэк: если синтеза в окружении нет (SSR, старый браузер) — не падаем.
//
// Замечание про автоплей: браузеры требуют жеста пользователя для старта озвучки.
// Клиент разблокирует аудио после первого клика по странице (см. main.ts).

export interface SpeakOptions {
  /** Прервать текущую/очередь и произнести немедленно. По умолчанию true. */
  flush?: boolean;
}

export class Speech {
  private synth: SpeechSynthesis | null;
  private enabled: boolean;
  private voice: SpeechSynthesisVoice | null = null;
  private lang = 'ru-RU';

  constructor(enabled = true) {
    this.synth =
      typeof window !== 'undefined' && 'speechSynthesis' in window
        ? window.speechSynthesis
        : null;
    this.enabled = enabled;
    this.loadVoice();
    // Список голосов может подгружаться асинхронно — перевыбираем при готовности.
    if (this.synth && 'onvoiceschanged' in this.synth) {
      this.synth.addEventListener('voiceschanged', () => this.loadVoice());
    }
  }

  /** Доступен ли синтез речи в текущем окружении. */
  get available(): boolean {
    return this.synth !== null;
  }

  /** Включён ли звук. */
  get isEnabled(): boolean {
    return this.enabled;
  }

  /** Включить/выключить звук. При выключении — обрываем текущую озвучку. */
  setEnabled(value: boolean): void {
    this.enabled = value;
    if (!value) this.cancel();
  }

  /** Переключить звук и вернуть новое состояние. */
  toggle(): boolean {
    this.setEnabled(!this.enabled);
    return this.enabled;
  }

  /**
   * Произнести текст на русском. При flush=true (по умолчанию) прерывает предыдущую реплику.
   * Тихо игнорируется, если звук выключен или синтез недоступен.
   */
  speak(text: string, opts: SpeakOptions = {}): void {
    if (!this.enabled || !this.synth || !text) return;
    const flush = opts.flush !== false;
    try {
      if (flush) this.synth.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.lang = this.lang;
      if (this.voice) u.voice = this.voice;
      this.synth.speak(u);
    } catch {
      // тихий фолбэк — озвучка не критична для работы табло
    }
  }

  /** Оборвать текущую озвучку и очередь. */
  cancel(): void {
    try {
      this.synth?.cancel();
    } catch {
      /* no-op */
    }
  }

  /** Выбрать предпочтительный ru-RU голос (если есть). */
  private loadVoice(): void {
    if (!this.synth) return;
    let voices: SpeechSynthesisVoice[] = [];
    try {
      voices = this.synth.getVoices();
    } catch {
      voices = [];
    }
    if (!voices.length) return;
    this.voice =
      voices.find((v) => v.lang === 'ru-RU') ??
      voices.find((v) => v.lang && v.lang.toLowerCase().startsWith('ru')) ??
      null;
  }
}
